/**
 * Admin Shell - Minimal admin interface foundation
 *
 * Built with Operational Architect design tokens.
 * Uses Lucide icons exclusively (no emoji).
 */
import React, { useState } from 'react';
import { useAuthStore } from '../store/auth';
import {
  LayoutDashboard,
  ClipboardList,
  Users,
  GraduationCap,
  Settings,
  LogOut,
  MessageSquare,
  TrendingUp,
  ClipboardCheck,
  File,
  Activity,
  Menu,
  X,
  User,
  UserCheck,
  Megaphone,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Mail,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { Button } from '@lms/ui';
import { ToastContainer } from './ToastContainer';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { campaignCopy } from '../copy/campaign-copy';
import { Bilingual } from './Bilingual';
import { useAutoLogout } from '../hooks/useAutoLogout';
import { useBrandingStore } from '../store/branding';
import { InactivityWarningModal } from './InactivityWarningModal';
import { NotificationPopover } from './NotificationPopover';
import { AnnouncementBanner } from './AnnouncementBanner';

export function AdminShell({ children }: { children: React.ReactNode }) {
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('admin_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('admin_sidebar_collapsed', String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const { isWarningOpen, countdown, stayLoggedIn, logoutNow } = useAutoLogout();

  const { settings: brandSettings } = useBrandingStore();
  const brandTitle = {
    id: brandSettings.titleId || copy.sidebar.brandName.id,
    ar: brandSettings.titleAr || copy.sidebar.brandName.ar,
  };
  const brandSubtitle = {
    id: brandSettings.subtitleId || copy.sidebar.brandSub.id,
    ar: brandSettings.subtitleAr || copy.sidebar.brandSub.ar,
  };

  // Map routes to page titles
  const getTitleFromPath = () => {
    const path = location.pathname;
    if (path === '/') return copy.sidebar.dashboard;
    if (path.startsWith('/programs')) return copy.sidebar.programs;
    if (path.startsWith('/materials/review')) return copy.sidebar.reviewQueue;
    if (path.startsWith('/materials')) return copy.sidebar.curriculum;
    if (path.startsWith('/registrations')) return copy.sidebar.registrations;
    if (path.startsWith('/evaluations')) return copy.sidebar.assessments;
    if (path.startsWith('/announcements')) return copy.sidebar.announcements;
    if (path.startsWith('/whatsapp-groups')) return copy.sidebar.communication;
    if (path.startsWith('/participants')) return copy.sidebar.monitoring;
    if (path.startsWith('/crm')) return copy.sidebar.crm;
    if (path.startsWith('/email-campaigns')) return campaignCopy.title;
    if (path.startsWith('/users')) return copy.sidebar.users;
    if (path.startsWith('/reports')) return copy.sidebar.intelligence;
    if (path.startsWith('/settings')) return copy.sidebar.settings;
    return copy.sidebar.dashboard;
  };

  const closeSidebar = () => setIsSidebarOpen(false);

  const sidebarTranslateClass = isSidebarOpen
    ? 'translate-x-0'
    : language === 'ar'
      ? 'translate-x-full md:translate-x-0'
      : '-translate-x-full md:translate-x-0';

  const sidebarWidthClass = isCollapsed ? 'md:w-[80px]' : 'md:w-[280px]';

  return (
    <div className="flex min-h-screen bg-surface text-on-surface font-body overflow-x-hidden">
      <ToastContainer />

      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden transition-opacity duration-300"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`w-[280px] ${sidebarWidthClass} bg-surface-container-low flex flex-col pt-6 pb-4 z-50 fixed h-screen border-outline-variant/10 transition-all duration-300 ease-in-out ${
          language === 'ar' ? 'right-0 border-l' : 'left-0 border-r'
        } ${sidebarTranslateClass}`}
      >
        {/* Logo/Brand & Collapse Toggle Header */}
        <div
          className={`px-4 pb-6 flex items-center justify-between ${isCollapsed ? 'md:px-2 md:justify-center' : 'px-6'}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              onClick={() => {
                if (isCollapsed) toggleCollapse();
              }}
              className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-surface-container-lowest shadow-level1 shrink-0 cursor-pointer"
              title={isCollapsed ? t(copy.shell.expandSidebar) : undefined}
            >
              <img
                alt="Tarbiyah Sunnah Learning Brand Logo"
                className="w-full h-full object-contain p-1.5"
                src="/logo.png"
              />
            </div>
            <div
              className={`transition-opacity duration-200 min-w-0 ${isCollapsed ? 'md:hidden' : 'block'}`}
            >
              <h1 className="text-sm font-extrabold font-headline text-primary leading-tight uppercase tracking-tight m-0 truncate">
                <Bilingual {...brandTitle} />
              </h1>
              <p className="text-[10px] text-on-surface-variant font-medium opacity-70 m-0 truncate">
                <Bilingual {...brandSubtitle} />
              </p>
            </div>
          </div>

          {/* Desktop Collapse Toggle Button in Sidebar Header */}
          <button
            type="button"
            onClick={toggleCollapse}
            className={`hidden md:flex p-1.5 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-colors shrink-0 ${
              isCollapsed ? 'hidden' : 'flex'
            }`}
            title={t(copy.shell.collapseSidebar)}
            aria-label={t(copy.shell.collapseSidebar)}
          >
            {language === 'ar' ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>

          {/* Mobile Close Drawer Button */}
          <button
            onClick={closeSidebar}
            className="p-1 text-on-surface-variant hover:text-primary rounded-lg md:hidden"
            aria-label={t(copy.shell.ariaCloseSidebar)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation List */}
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {user?.roles?.some(role =>
            ['ADMIN', 'SUPERADMIN', 'SUPER_ADMIN'].includes(
              (typeof role === 'string' ? role : role.role.name).toUpperCase()
            )
          ) && (
            <NavItem
              label={campaignCopy.title}
              icon={Mail}
              href="/email-campaigns"
              onClick={closeSidebar}
              isCollapsed={isCollapsed}
              language={language}
            />
          )}
          <NavItem
            label={copy.sidebar.dashboard}
            icon={LayoutDashboard}
            href="/"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.registrations}
            icon={ClipboardList}
            href="/registrations"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.programs}
            icon={File}
            href="/programs"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.assessments}
            icon={GraduationCap}
            href="/evaluations"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.announcements}
            icon={Megaphone}
            href="/announcements"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.communication}
            icon={MessageSquare}
            href="/whatsapp-groups"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.monitoring}
            icon={Activity}
            href="/participants"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.crm}
            icon={UserCheck}
            href="/crm"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.users}
            icon={Users}
            href="/users"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.intelligence}
            icon={TrendingUp}
            href="/reports"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.reviewQueue}
            icon={ClipboardCheck}
            href="/materials/review"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.curriculum}
            icon={Settings}
            href="/materials"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
        </nav>

        {/* Bottom Actions */}
        <div className="px-3 pt-3 mt-auto border-t border-on-surface/5 space-y-1">
          <NavItem
            label={{ id: 'Profil Saya', ar: 'ملفي الشخصي' }}
            icon={User}
            href="/profile"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />
          <NavItem
            label={copy.sidebar.settings}
            icon={Settings}
            href="/settings"
            onClick={closeSidebar}
            isCollapsed={isCollapsed}
            language={language}
          />

          {/* Logout Button */}
          <div className="relative group">
            <Button
              variant="ghost"
              onClick={() => {
                closeSidebar();
                logout();
              }}
              className={`w-full !justify-start text-error hover:bg-error-container/10 rounded-xl transition-all duration-200 ${
                isCollapsed ? 'md:!justify-center md:px-0 px-4 py-3' : 'gap-3 px-4 py-3'
              }`}
              title={isCollapsed ? t(copy.sidebar.logout) : undefined}
            >
              <LogOut size={18} className="shrink-0" />
              <span className={`font-medium ${isCollapsed ? 'md:hidden' : 'inline'}`}>
                <Bilingual {...copy.sidebar.logout} />
              </span>
            </Button>
            {isCollapsed && (
              <div
                className={`hidden md:group-hover:flex absolute ${
                  language === 'ar' ? 'right-full mr-3' : 'left-full ml-3'
                } top-1/2 -translate-y-1/2 z-50 items-center px-3 py-1.5 bg-neutral-900 text-white rounded-lg shadow-level3 text-xs font-semibold whitespace-nowrap pointer-events-none transition-all duration-150 animate-fadeIn`}
              >
                <Bilingual {...copy.sidebar.logout} />
              </div>
            )}
          </div>

          {/* Desktop Footer Expand/Collapse Toggle */}
          <div className="pt-2 hidden md:block">
            <button
              type="button"
              onClick={toggleCollapse}
              className={`w-full flex items-center p-2 rounded-xl text-on-surface-variant hover:text-primary hover:bg-surface-container-high transition-all text-xs font-bold ${
                isCollapsed ? 'justify-center' : 'justify-start gap-2.5 px-3'
              }`}
              title={isCollapsed ? t(copy.shell.expandSidebar) : t(copy.shell.collapseSidebar)}
              aria-label={isCollapsed ? t(copy.shell.expandSidebar) : t(copy.shell.collapseSidebar)}
            >
              {isCollapsed ? (
                language === 'ar' ? (
                  <ChevronLeft size={18} />
                ) : (
                  <ChevronRight size={18} />
                )
              ) : (
                <>
                  {language === 'ar' ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                  <span className="truncate">
                    <Bilingual {...copy.shell.collapseSidebar} />
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out ${
          language === 'ar'
            ? isCollapsed
              ? 'mr-0 md:mr-[80px]'
              : 'mr-0 md:mr-[280px]'
            : isCollapsed
              ? 'ml-0 md:ml-[80px]'
              : 'ml-0 md:ml-[280px]'
        }`}
      >
        {/* Header (Glassmorphism) */}
        <header className="h-16 bg-white/80 backdrop-blur-xl px-4 sm:px-8 flex items-center justify-between sticky top-0 z-30 shadow-level1">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Menu Toggle Button */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className={`p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg md:hidden ${
                language === 'ar' ? '-mr-2' : '-ml-2'
              }`}
              aria-label={t(copy.shell.ariaOpenSidebar)}
            >
              <Menu size={20} />
            </button>

            {/* Desktop Quick Sidebar Collapse / Expand Toggle Button */}
            <button
              onClick={toggleCollapse}
              className="p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg hidden md:flex items-center justify-center transition-colors -ml-2"
              title={isCollapsed ? t(copy.shell.expandSidebar) : t(copy.shell.collapseSidebar)}
              aria-label={isCollapsed ? t(copy.shell.expandSidebar) : t(copy.shell.collapseSidebar)}
            >
              {isCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
            </button>

            <h2 className="m-0 text-sm sm:text-base font-headline font-bold text-primary tracking-tight">
              <Bilingual {...getTitleFromPath()} />
            </h2>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            {/* Language Switcher */}
            <div className="flex items-center bg-surface-container rounded-lg p-0.5 sm:p-1 border border-on-surface/5">
              <button
                onClick={() => setLanguage('id')}
                className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-bold rounded transition-all ${
                  language === 'id'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                ID
              </button>
              <button
                onClick={() => setLanguage('ar')}
                className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-bold rounded transition-all ${
                  language === 'ar'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                AR
              </button>
            </div>

            {/* Notification & Announcement Popover */}
            <NotificationPopover />

            <div className="h-6 w-px bg-outline-variant/30"></div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-on-surface leading-tight m-0">
                  {user?.name || t(copy.shell.defaultUserName)}
                </p>
                <p className="text-[10px] text-on-surface-variant font-medium uppercase m-0">
                  {(() => {
                    const firstRole = user?.roles?.[0];
                    if (
                      typeof firstRole === 'object' &&
                      firstRole !== null &&
                      'role' in firstRole
                    ) {
                      return (
                        (firstRole as { role?: { name?: string } })?.role?.name ||
                        t(copy.shell.defaultRoleName)
                      );
                    }
                    return (firstRole as string) || t(copy.shell.defaultRoleName);
                  })()}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-surface-container-highest flex items-center justify-center text-primary font-headline font-bold overflow-hidden shadow-level1">
                {(user?.name || 'A')[0].toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Urgent Announcement Broadcast Banner */}
        <AnnouncementBanner />

        {/* Page Content */}
        <div className="flex-1 p-4 sm:p-6 md:p-10 overflow-auto bg-mesh">
          <div className="max-w-[1400px] mx-auto">{children}</div>
        </div>
      </main>

      <InactivityWarningModal
        isOpen={isWarningOpen}
        countdown={countdown}
        onStayLoggedIn={stayLoggedIn}
        onLogoutNow={logoutNow}
      />
    </div>
  );
}

interface NavItemProps {
  label: { id: string; ar: string };
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href: string;
  onClick?: () => void;
  isCollapsed?: boolean;
  language?: 'id' | 'ar';
}

const NavItem = React.memo(function NavItem({
  label,
  icon: Icon,
  href,
  onClick,
  isCollapsed,
  language,
}: NavItemProps) {
  return (
    <div className="relative group">
      <NavLink
        to={href}
        end={href === '/'}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center w-full rounded-xl cursor-pointer text-sm font-body transition-all duration-150 outline-none focus:outline-none ${
            isCollapsed ? 'md:justify-center md:px-0 px-4 py-3' : 'gap-3 px-4 py-3'
          } ${
            isActive
              ? 'bg-surface-container-lowest text-primary font-bold shadow-level1'
              : 'bg-transparent text-on-surface-variant hover:text-primary hover:bg-surface-container-lowest/50'
          }`
        }
      >
        <Icon size={18} className="shrink-0" />
        <span className={`truncate ${isCollapsed ? 'md:hidden' : 'inline'}`}>
          <Bilingual {...label} />
        </span>
      </NavLink>

      {/* Floating tooltip on collapsed hover */}
      {isCollapsed && (
        <div
          className={`hidden md:group-hover:flex absolute ${
            language === 'ar' ? 'right-full mr-3' : 'left-full ml-3'
          } top-1/2 -translate-y-1/2 z-50 items-center px-3 py-1.5 bg-neutral-900 text-white rounded-lg shadow-level3 text-xs font-semibold whitespace-nowrap pointer-events-none transition-all duration-100 animate-fadeIn border border-neutral-800`}
        >
          <Bilingual {...label} />
        </div>
      )}
    </div>
  );
});
